// Effets sur le cache TanStack des messages reçus par useGlobalRealtime (spec
// 2026-10-10 §3 webapp, couverture §5 lignes 25–34). Les lignes `matches` /
// `match_assignments` patchent le détail `['matches', id]` (modale de
// désignation ouverte) depuis `record`, un DELETE retire par l'`id` du ping ;
// puis chaque message invalide les clés réellement lues par les écrans,
// regroupées par clé fixe sur 300 ms (coalesce.ts attend la fin du
// rafraîchissement avant d'en relancer un). Chaque table a UN jeu
// d'invalidations, partagé par le message et le resync de son sujet : ils ne
// peuvent pas diverger. Le sujet public `system` n'est pas fiable : son ping ne
// déclenche qu'un rafraîchissement limité (spec I4).
import type { QueryClient } from '@tanstack/react-query';
import { AccountingStatus, MatchStatus, type Assignment, type Match } from '../../types';
import type { PaginatedResponse } from '../../types/query.types';
import { mapAssignment } from '../mappers';
import { APP_SETTINGS_QUERY_KEY } from '../../hooks/useAppSettings';
import { LEAGUES_QUERY_KEY } from '../../hooks/useLeagues';
import { LOCATIONS_QUERY_KEY } from '../../hooks/useLocations';
import { SANCTION_DERIVED_QUERY_KEYS } from '../../hooks/sanctionQueryKeys';
import { QUERY_KEY_PREFIX as POINT_ADJUSTMENTS_QUERY_KEY_PREFIX } from '../../hooks/usePointAdjustments';
import { MANAGEMENT_TAB_COUNTS_QUERY_KEY_PREFIX } from '../../hooks/useManagementTabCounts';
import { OFFICIAL_ASSIGNMENT_COUNTS_QUERY_KEY_PREFIX } from '../../hooks/useOfficialAssignmentCounts';
import { OFFICIAL_RECENCY_QUERY_KEY_PREFIX } from '../../hooks/useOfficialRecencyFlags';
import { PLAYERS_LOOKUP_QUERY_KEY_PREFIX } from '../../hooks/usePlayers';
import { PAYMENTS_QUERY_KEY_PREFIX } from '../../hooks/usePayments';
import { INFINITE_PAYMENTS_QUERY_KEY_PREFIX } from '../../hooks/useInfinitePayments';
import { STADIUM_AUDIT_QUERY_KEY } from '../../hooks/useStadiumAuditPlanner';
import { MISSION_ORDER_NUMBERING_QUERY_KEY_PREFIXES } from '../../hooks/useMissionOrderCounters';
import { SUSPENSION_VIOLATIONS_QUERY_KEY_PREFIX } from '../../hooks/useSuspensionViolations';
import { DISCIPLINARY_AUDIT_QUERY_KEY_PREFIX } from '../../hooks/useDisciplinaryAuditServer';
import { DISCIPLINARY_AUDIT_ISSUES_COUNT_KEY } from '../../hooks/useDisciplinaryAuditIssuesCount';
import { MAINTENANCE_QUERY_KEY } from '../../utils/maintenance';
import { REFEREE_PROPOSALS_QUERY_KEY_PREFIX } from '../refereeProposals/refereeProposals';
import { createCoalescer, createThrottle, type Coalescer, type Throttle } from './coalesce';
import { parseChange, type Change } from './payloads';

export const LEAGUE_EVENTS = [
    'match_assignments_change',
    'matches_change',
    'point_adjustments_change',
    'sanctions_change',
    'mission_orders_change',
    'referee_assignment_proposals_change',
] as const;
export const ADMIN_EVENTS = [
    'officials_change',
    'official_emails_change',
    'players_change',
    'leagues_change',
    'app_settings_versions_change',
    'locations_change',
    'mission_order_batches_change',
    'jobs_change',
    'job_chunks_change',
] as const;
export const USER_EVENTS = ['jobs_change', 'job_chunks_change'] as const;
export const SYSTEM_EVENTS = ['maintenance_change'] as const;

type LeagueEvent = (typeof LEAGUE_EVENTS)[number];
type AdminEvent = (typeof ADMIN_EVENTS)[number];
type UserEvent = (typeof USER_EVENTS)[number];
/** Invalidations d'une table ; `null` = resync du sujet (pas de message précis). */
type Refresh = (change: Change | null) => void;

export interface GlobalRealtimeHandlers {
    onLeagueMessage(event: string, payload: Record<string, unknown>): void;
    onAdminMessage(event: string, payload: Record<string, unknown>): void;
    onUserMessage(event: string, payload: Record<string, unknown>): void;
    onSystemMessage(event: string, payload: Record<string, unknown>): void;
    onLeagueResync(): void;
    onAdminResync(): void;
    onUserResync(): void;
    onSystemResync(): void;
    dispose(): void;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

function mapAccountingStatus(status: unknown, fallback: AccountingStatus): AccountingStatus {
    switch (status) {
        case 'not_entered':
            return AccountingStatus.NOT_ENTERED;
        case 'pending_validation':
            return AccountingStatus.PENDING_VALIDATION;
        case 'validated':
            return AccountingStatus.VALIDATED;
        case 'rejected':
            return AccountingStatus.REJECTED;
        case 'closed':
            return AccountingStatus.CLOSED;
        default:
            return fallback;
    }
}

function mapMatchStatus(status: unknown, fallback: Match['status']): Match['status'] {
    switch (status) {
        case 'scheduled':
            return MatchStatus.SCHEDULED;
        case 'in_progress':
            return MatchStatus.IN_PROGRESS;
        case 'completed':
            return MatchStatus.COMPLETED;
        case 'postponed':
            return MatchStatus.POSTPONED;
        case 'cancelled':
            return MatchStatus.CANCELLED;
        default:
            return fallback;
    }
}

const jobIdOf = (change: Change): string | null => (change.kind === 'row' ? str(change.record.id) : change.id);
const parentJobIdOf = (change: Change): string | null => (change.kind === 'row' ? str(change.record.parent_job_id) : null);

export function createGlobalRealtimeHandlers(
    queryClient: QueryClient,
    deps: { coalescer?: Coalescer; throttle?: Throttle } = {},
): GlobalRealtimeHandlers {
    const coalescer = deps.coalescer ?? createCoalescer();
    const throttle = deps.throttle ?? createThrottle();

    /**
     * Une clé de regroupement fixe → une ou plusieurs racines invalidées par
     * préfixe. La promesse est rendue au regroupeur, qui ne relance pas la clé
     * tant que le rafraîchissement n'est pas terminé.
     */
    const invalidate = (key: string, ...queryKeys: (readonly unknown[])[]) =>
        coalescer.schedule(key, () => Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))));

    // Seul patch : le détail ['matches', id] (useMatch, modale de désignation
    // ouverte) et l'ancienne forme PaginatedResponse de useMatches. Les écrans
    // de liste (Match[], InfiniteData) sont rafraîchis par invalidateMatches.
    const updateMatchesCache = (matchId: string, updater: (match: Match) => Match) => {
        queryClient.setQueriesData<PaginatedResponse<Match>>({ queryKey: ['matches'] }, (existing) => {
            if (!existing?.data) return existing;
            let mutated = false;
            const data = existing.data.map((match) => {
                if (match.id !== matchId) return match;
                mutated = true;
                return updater(match);
            });
            return mutated ? { ...existing, data } : existing;
        });
        queryClient.setQueryData<Match>(['matches', matchId], (existing) => (existing ? updater(existing) : existing));
    };

    const patchAssignments = (matchId: string, update: (assignments: Assignment[]) => Assignment[]) =>
        updateMatchesCache(matchId, (match) => ({ ...match, assignments: update(match.assignments ?? []) }));

    const upsertAssignment = (mapped: Assignment) => (assignments: Assignment[]) => {
        const index = assignments.findIndex((a) => a.id === mapped.id);
        if (index === -1) return [...assignments, mapped];
        const copy = assignments.slice();
        copy[index] = { ...assignments[index], ...mapped };
        return copy;
    };

    // Tous les écrans de matchs : semaine, urgents, calendrier, kanban
    // comptable, focus journée, classements, détail, compteurs de saisie
    // disciplinaire — toutes sous la racine ['matches'].
    const invalidateMatches = () => invalidate('matches', ['matches']);
    const invalidatePlanningOverview = () => invalidate('planning-overview', ['planning-overview']);
    const invalidateLeagues = () => invalidate('leagues', LEAGUES_QUERY_KEY);
    const invalidateAppSettings = () => invalidate('app_settings_versions', APP_SETTINGS_QUERY_KEY);
    const invalidateLocations = () => invalidate('locations', LOCATIONS_QUERY_KEY);

    // Virements (usePayments) et Finances (useInfinitePayments) lisent
    // match_assignments joint à matches.accounting_status ; aucune de leurs
    // clés ne porte de matchId, donc tout le préfixe.
    const invalidatePayments = () => {
        invalidate('payments', [PAYMENTS_QUERY_KEY_PREFIX]);
        invalidate('infinite-payments', [INFINITE_PAYMENTS_QUERY_KEY_PREFIX]);
    };

    // Les écrans réels lisent des clés dérivées, pas la table brute : les
    // ajustements de points alimentent les classements via `pointAdjustments`
    // (hooks/usePointAdjustments.ts), les officiels via `league-officials*`
    // (hooks/useOfficialScaling.ts, useLeagueOfficialsStatsLite.ts) et les
    // joueurs via `leaguePlayers` (components/management/routes/PlayersRoute.tsx).
    const invalidatePointAdjustments = () =>
        invalidate('point_adjustments', ['point_adjustments'], [POINT_ADJUSTMENTS_QUERY_KEY_PREFIX]);

    const invalidateOfficials = () =>
        invalidate('officials', ['officials'], ['league-officials'], ['league-officials-stats'], ['league-officials-counts']);

    const invalidatePlayers = () =>
        invalidate(
            'players',
            ['players'],
            ['leaguePlayers'],
            // `get_management_tab_counts` counts players (and staff/transfers/audit,
            // none from this table) — the badge on PlayersRoute/StaffRoute/
            // ManagementContainer needs the same refresh.
            [MANAGEMENT_TAB_COUNTS_QUERY_KEY_PREFIX],
            // `usePlayersLookup` (name/id lookups used in disciplinary pickers) reads the
            // same `players` table through `get_league_players` — same staleness bug.
            [PLAYERS_LOOKUP_QUERY_KEY_PREFIX],
        );

    // Désignations par officiel, affichées sur GameDayFocusView (RPC
    // get_official_assignment_counts) ; aucune portée de ligue fiable sur les
    // deux chemins (ligne ou ping), donc on invalide tout le préfixe.
    const invalidateOfficialAssignmentCounts = () =>
        invalidate('official-assignment-counts', [OFFICIAL_ASSIGNMENT_COUNTS_QUERY_KEY_PREFIX]);

    // Drapeaux de récence (RPC get_official_recency_flags, lit match_assignments) —
    // affichés dans les modales de désignation ; même raisonnement de portée que
    // ci-dessus, préfixe entier invalidé.
    const invalidateOfficialRecency = () => invalidate('official-recency', [OFFICIAL_RECENCY_QUERY_KEY_PREFIX]);

    const invalidateRefereeProposals = () => invalidate('referee-proposals', [REFEREE_PROPOSALS_QUERY_KEY_PREFIX]);

    const invalidateOfficialEmails = () =>
        invalidate(
            'official_emails',
            ['official_emails'],
            ['officials'],
            ['league-officials'],
            ['league-officials-stats'],
            ['league-officials-counts'],
        );

    // Au-delà des listes et des dérivées partagées avec les mutations locales :
    // l'audit disciplinaire et les violations de suspension (caches serveur
    // rendus périmés par le déclencheur invalidate_disciplinary_caches_on_sanctions)
    // et le badge Audit, qui compte directement dans `sanctions`.
    const invalidateSanctions = () =>
        invalidate(
            'sanctions',
            ...['sanctions', 'paginatedSanctions', 'sanctionsCount', ...SANCTION_DERIVED_QUERY_KEYS].map((key) => [key]),
            [DISCIPLINARY_AUDIT_QUERY_KEY_PREFIX],
            [SUSPENSION_VIOLATIONS_QUERY_KEY_PREFIX],
            [DISCIPLINARY_AUDIT_ISSUES_COUNT_KEY],
        );

    // Lecteurs réels de mission_orders / mission_order_batches : le
    // planificateur d'audit des stades (fonction stadium-audit-planner, numéros
    // et envois d'ordres) et la numérotation (maxOrderNumber lit mission_orders ;
    // les compteurs avancent à chaque numéro attribué). Le badge « ordre envoyé »
    // vient de match_assignments, donc de la ligne 25.
    const invalidateMissionOrders = () =>
        invalidate('mission_orders', STADIUM_AUDIT_QUERY_KEY, ...MISSION_ORDER_NUMBERING_QUERY_KEY_PREFIXES.map((key) => [key]));

    const invalidateJobData = (jobId: string | null) =>
        coalescer.schedule(`jobs:${jobId ?? 'all'}`, () =>
            Promise.all([
                ...(jobId ? [queryClient.invalidateQueries({ queryKey: ['jobs', jobId], exact: true })] : []),
                queryClient.invalidateQueries({ queryKey: ['jobs'] }),
                queryClient.invalidateQueries({ queryKey: ['job-center'] }),
                queryClient.invalidateQueries({ queryKey: ['job-details'] }),
            ]),
        );

    const refreshMaintenance = () =>
        throttle.run(() => {
            void queryClient.invalidateQueries({ queryKey: MAINTENANCE_QUERY_KEY });
        });

    // Un jeu d'invalidations par table, appelé par chaque message (après le
    // patch éventuel) et par le resync de son sujet.
    const leagueRefresh: Record<LeagueEvent, Refresh> = {
        match_assignments_change: () => {
            invalidateMatches();
            invalidatePlanningOverview();
            invalidatePayments();
            invalidateOfficialAssignmentCounts();
            invalidateOfficialRecency();
        },
        matches_change: () => {
            invalidateMatches();
            invalidatePlanningOverview();
            invalidatePayments();
        },
        point_adjustments_change: invalidatePointAdjustments,
        sanctions_change: invalidateSanctions,
        mission_orders_change: invalidateMissionOrders,
        referee_assignment_proposals_change: invalidateRefereeProposals,
    };
    const jobsRefresh: Record<UserEvent, Refresh> = {
        jobs_change: (change) => invalidateJobData(change ? jobIdOf(change) : null),
        job_chunks_change: (change) => invalidateJobData(change ? parentJobIdOf(change) : null),
    };
    const adminRefresh: Record<AdminEvent, Refresh> = {
        officials_change: invalidateOfficials,
        official_emails_change: invalidateOfficialEmails,
        players_change: invalidatePlayers,
        leagues_change: () => {
            invalidateLeagues();
            invalidatePlanningOverview();
        },
        app_settings_versions_change: () => {
            invalidateAppSettings();
            invalidatePlanningOverview();
        },
        locations_change: invalidateLocations,
        mission_order_batches_change: invalidateMissionOrders,
        ...jobsRefresh,
    };

    const resyncAll = (refresh: Record<string, Refresh>) => {
        for (const run of Object.values(refresh)) run(null);
    };
    const refreshFor = (refresh: Record<string, Refresh>, event: string, change: Change) => {
        if (Object.prototype.hasOwnProperty.call(refresh, event)) refresh[event](change);
    };

    function patchAssignment(change: Change): void {
        if (change.kind === 'row') {
            const matchId = str(change.record.match_id);
            if (matchId) patchAssignments(matchId, upsertAssignment(mapAssignment(change.record)));
            return;
        }
        // DELETE, ou désignation sortie de ce sujet : retirée par l'id du ping.
        const { id, matchId } = change;
        if (id && matchId) patchAssignments(matchId, (list) => list.filter((a) => a.id !== id));
    }

    function patchMatch(change: Change): void {
        if (change.kind !== 'row' || change.op !== 'UPDATE') return;
        const row = change.record as Record<string, any>;
        const matchId = str(row.id);
        if (!matchId) return;
        updateMatchesCache(matchId, (match) => ({
            ...match,
            matchDate: row.match_date ?? match.matchDate,
            matchTime: row.match_time ?? match.matchTime,
            status: row.status ? mapMatchStatus(row.status, match.status) : match.status,
            isSheetSent: typeof row.is_sheet_sent === 'boolean' ? row.is_sheet_sent : match.isSheetSent,
            hasUnsentChanges: typeof row.has_unsent_changes === 'boolean' ? row.has_unsent_changes : match.hasUnsentChanges,
            isArchived: typeof row.is_archived === 'boolean' ? row.is_archived : match.isArchived,
            accountingStatus: mapAccountingStatus(row.accounting_status, match.accountingStatus),
            rejectionReason: row.rejection_reason ?? match.rejectionReason,
            rejectionComment: row.rejection_comment ?? match.rejectionComment,
            validatedBy: row.validated_by ?? match.validatedBy,
            validatedAt: row.validated_at ?? match.validatedAt,
            accountingPeriodId: row.accounting_period_id ?? match.accountingPeriodId,
        }));
    }

    return {
        onLeagueMessage(event, payload) {
            const change = parseChange(payload);
            if (event === 'match_assignments_change') patchAssignment(change);
            else if (event === 'matches_change') patchMatch(change);
            refreshFor(leagueRefresh, event, change);
        },
        onAdminMessage(event, payload) {
            refreshFor(adminRefresh, event, parseChange(payload));
        },
        onUserMessage(event, payload) {
            refreshFor(jobsRefresh, event, parseChange(payload));
        },
        onSystemMessage() {
            refreshMaintenance();
        },
        onLeagueResync() {
            resyncAll(leagueRefresh);
        },
        onAdminResync() {
            resyncAll(adminRefresh);
        },
        onUserResync() {
            resyncAll(jobsRefresh);
        },
        onSystemResync() {
            refreshMaintenance();
        },
        dispose() {
            coalescer.cancelAll();
            throttle.cancel();
        },
    };
}
